import { z } from "zod";

/** Data-source filters: a codec per URL param name, like the column codecs. */
export type DataParamsCodecMap = Record<string, z.ZodCodec>;

export type DataParamValues<M extends DataParamsCodecMap> = {
    [K in keyof M]: z.infer<M[K]>;
};

export interface DataParamsApi<M extends DataParamsCodecMap> {
    values: DataParamValues<M>;
    setValue: <K extends keyof M>(key: K, value: DataParamValues<M>[K]) => void;
    reset: () => void;
}

/** Raw URL value (`null` = absent) → value, falling back to default on garbage. */
export function decodeDataParam(
    codec: z.ZodCodec,
    raw: string | null,
    defaultValue: unknown
): unknown {
    if (raw === null) {
        return defaultValue;
    }
    const result = codec.safeDecode(raw);
    return result.success ? result.data : defaultValue;
}

/** Validate a value read from localStorage (native form); garbage → default. */
export function coerceDataParam(
    codec: z.ZodCodec,
    stored: unknown,
    defaultValue: unknown
): unknown {
    return codec.safeEncode(stored).success ? stored : defaultValue;
}

/**
 * Resolves every data param for a render. Precedence: URL → default, so the
 * URL alone describes the view. The stored values only come back through a
 * `restore` URL.
 */
export function resolveDataParams<M extends DataParamsCodecMap>(
    codecMap: M,
    urlParams: URLSearchParams,
    dataDefaults: Record<string, unknown>
): DataParamValues<M> {
    return Object.fromEntries(
        Object.entries(codecMap).map(([key, codec]) => [
            key,
            decodeDataParam(codec, urlParams.get(key), dataDefaults[key]),
        ])
    ) as DataParamValues<M>;
}

export function deleteDataParams(
    params: URLSearchParams,
    codecMap: DataParamsCodecMap
): void {
    Object.keys(codecMap).forEach((key) => params.delete(key));
}

/** value → URL string; `null` drops the param (equals default, or can't encode). */
export function encodeDataParam(
    codec: z.ZodCodec,
    value: unknown,
    defaultValue: unknown
): string | null {
    const encoded = codec.safeEncode(value);
    if (!encoded.success) {
        return null;
    }
    // Drop-on-default by comparing the encoded form (works for any type).
    const encodedDefault = codec.safeEncode(defaultValue);
    if (encodedDefault.success && encoded.data === encodedDefault.data) {
        return null;
    }
    return encoded.data as string;
}
